import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import mascot from "@/assets/mascot.png";
import { useLang } from "@/hooks/use-language";
import {
  listPublicContentByValue,
  type ValueContent,
  type ValueRow,
} from "@/lib/values.functions";

type LoaderData = { value: ValueRow | null; items: ValueContent[] };

export const Route = createFileRoute("/values/$code")({
  loader: async ({ params }) => {
    const data = await listPublicContentByValue({ data: { code: params.code } });
    if (!data.value) throw notFound();
    return data;
  },
  head: ({ loaderData }) => {
    const v = (loaderData as LoaderData | undefined)?.value;
    const title = v
      ? `${v.label_en} | ${v.label_ar} — Jozori`
      : "Value — Jozori";
    const desc = v
      ? `${v.description_en ?? `Stories and activities that nurture ${v.label_en.toLowerCase()} in children.`} · ${v.description_ar ?? `قصص وأنشطة تغرس قيمة ${v.label_ar} في الأطفال.`}`
      : "Jozori educational value";
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: `https://jozori.com/values/${v?.code ?? ""}` },
      ],
      links: v
        ? [{ rel: "canonical", href: `https://jozori.com/values/${v.code}` }]
        : [],
    };
  },
  component: ValueDetail,
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-3xl font-bold">Value not found</h1>
        <Link to="/values" className="mt-4 inline-block text-primary underline">
          Back to values
        </Link>
      </div>
    </div>
  ),
});

const KIND_EMOJI: Record<string, string> = {
  story: "📖",
  video: "🎬",
  quiz: "❓",
  printable: "🖨️",
  activity: "✨",
  challenge: "🎯",
  coloring: "🎨",
  worksheet: "📄",
};
function kindEmoji(k: string) {
  return KIND_EMOJI[k] ?? "✨";
}

function ValueDetail() {
  const { value, items } = Route.useLoaderData() as LoaderData;
  const { lang, dir, toggle } = useLang();
  const isAr = lang === "ar";
  if (!value) return null;

  return (
    <div dir={dir} className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-clay/30 bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center gap-2 font-display-en text-lg font-black">
            <img src={mascot} alt="" className="size-8 rounded-full ring-2 ring-primary/30" />
            <span>Jozori</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/values"
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
            >
              {isAr ? "كل القيم" : "All values"}
            </Link>
            <button
              onClick={toggle}
              className="rounded-full border border-clay/40 bg-card px-3 py-1.5 text-xs font-bold hover:bg-clay/20"
            >
              {isAr ? "English" : "العربية"}
            </button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-gradient-to-br from-primary/15 via-accent/10 to-secondary/15"
        />
        <div className="relative mx-auto max-w-4xl px-4 py-16 text-center">
          <div className="text-6xl mb-4">{value.emoji}</div>
          <h1
            className={`text-4xl md:text-5xl font-black ${
              isAr ? "font-display-ar" : "font-display-en"
            }`}
          >
            {isAr ? value.label_ar : value.label_en}
          </h1>
          {(isAr ? value.description_ar : value.description_en) && (
            <p className="mx-auto mt-4 max-w-2xl text-foreground/70">
              {isAr ? value.description_ar : value.description_en}
            </p>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-12">
        <h2
          className={`mb-6 text-2xl font-black ${
            isAr ? "font-display-ar" : "font-display-en"
          }`}
        >
          {isAr ? "محتوى يغرس هذه القيمة" : "Content that nurtures this value"}
        </h2>
        {items.length === 0 ? (
          <p className="text-foreground/60">
            {isAr
              ? "لا يوجد محتوى مجاني منشور بعد لهذه القيمة — تابعنا قريبًا."
              : "No free published content yet for this value — check back soon."}
          </p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((it) => (
              <article
                key={it.id}
                className="rounded-3xl border-2 border-clay/30 bg-card overflow-hidden hover:border-primary hover:-translate-y-1 transition-all"
              >
                {it.cover_url ? (
                  <img
                    src={it.cover_url}
                    alt=""
                    loading="lazy"
                    className="aspect-[3/2] w-full object-cover"
                  />
                ) : (
                  <div className="aspect-[3/2] w-full bg-gradient-to-br from-primary/20 to-accent/20" />
                )}
                <div className="p-4">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-foreground/50">
                    {kindEmoji(it.kind)} {it.kind} · {it.age_group}
                  </p>
                  <h3
                    className={`mt-1 font-bold leading-tight line-clamp-2 ${
                      isAr ? "font-display-ar" : "font-display-en"
                    }`}
                  >
                    {isAr ? it.title_ar : it.title_en}
                  </h3>
                  {(isAr ? it.description_ar : it.description_en) && (
                    <p className="mt-1 text-xs text-foreground/60 line-clamp-2">
                      {isAr ? it.description_ar : it.description_en}
                    </p>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <Link
            to="/app"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground hover:bg-primary/90"
          >
            {isAr ? "افتح التطبيق" : "Open the app"}
          </Link>
        </div>
      </section>
    </div>
  );
}
